import { useEffect, useRef, useState } from "react";
import { EyeOff, Hash, KeyRound, TriangleAlert, X } from "lucide-react";
import { useT } from "@/src/i18n";
import {
  ROOM_KEY_DM_ONLY_FIELDS,
  ROOM_KEY_SENSE_FIELDS,
  ROOM_KEY_TEXT_FIELDS,
  isOrphanedRoomKey,
  roomNumberFor,
  type RoomKeySenseField,
  type RoomKeyTextField,
  type RoomKeyUpdate,
} from "@/src/core/roomKeys";
import { useMapStore } from "@/src/store/useMapStore";
import type { RoomKey, RoomKeySenses } from "@/src/types/map";

interface RoomKeyPanelProps {
  /** The annotation this key binds to. The panel is remounted when it changes. */
  objectId: string;
  onClose: () => void;
}

/**
 * Every field as a plain string, so no input is ever uncontrolled and "absent"
 * versus "empty" is decided once, at commit time, rather than per keystroke.
 *
 * Typing it as a Record over the field unions means adding a field to the model
 * fails to compile here until the panel is taught about it.
 */
type Draft = Record<RoomKeyTextField, string> & { senses: Record<RoomKeySenseField, string> };

const draftFrom = (key: RoomKey | undefined): Draft => ({
  name: key?.name ?? "",
  description: key?.description ?? "",
  readAloud: key?.readAloud ?? "",
  lighting: key?.lighting ?? "",
  dmNotes: key?.dmNotes ?? "",
  senses: {
    smell: key?.senses?.smell ?? "",
    sound: key?.senses?.sound ?? "",
    temperature: key?.senses?.temperature ?? "",
    texture: key?.senses?.texture ?? "",
  },
});

const labelClass = "block text-[9px] font-bold uppercase tracking-[0.14em] text-stone-600";
const fieldClass =
  "mt-1.5 w-full rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs " +
  "font-medium normal-case tracking-normal text-stone-100 outline-none transition " +
  "placeholder:text-stone-600 focus:border-[#c3463d]";

export function RoomKeyPanel({ objectId, onClose }: RoomKeyPanelProps) {
  const stored = useMapStore((state) => state.roomKeys[objectId]);
  const objects = useMapStore((state) => state.objects);
  const setRoomKey = useMapStore((state) => state.setRoomKey);
  const t = useT();
  const [draft, setDraft] = useState<Draft>(() => draftFrom(stored));
  // Mirrored into a ref so the teardown flush below can read the latest draft
  // without taking it as a dependency and re-registering on every keystroke.
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  });

  const number = roomNumberFor(objectId, objects);
  const orphaned = isOrphanedRoomKey(objectId, objects);

  // Written out rather than spread with a computed key: a computed key widens
  // the result to an index signature, which no longer satisfies Draft.
  const setTextDraft = (field: RoomKeyTextField, value: string) =>
    setDraft((current) => {
      const next: Draft = { ...current, senses: { ...current.senses } };
      next[field] = value;
      return next;
    });

  const setSenseDraft = (field: RoomKeySenseField, value: string) =>
    setDraft((current) => {
      const senses = { ...current.senses };
      senses[field] = value;
      return { ...current, senses };
    });

  /**
   * Commit on blur, not per keystroke: a paragraph of read-aloud text is one
   * undo step rather than one per character. An unchanged field is not sent at
   * all, so tabbing through the panel costs nothing (D-025).
   */
  const commitText = (field: RoomKeyTextField) => {
    const value = draftRef.current[field];
    if ((stored?.[field] ?? "") === value) return;
    const updates: RoomKeyUpdate = {};
    updates[field] = value;
    setRoomKey(objectId, updates);
  };

  const commitSense = (field: RoomKeySenseField) => {
    const value = draftRef.current.senses[field];
    if ((stored?.senses?.[field] ?? "") === value) return;
    const senses: Partial<RoomKeySenses> = {};
    senses[field] = value;
    setRoomKey(objectId, { senses });
  };

  /**
   * Closing the panel, or switching to another room, can happen while a field
   * still has focus and has not blurred. Flush whatever is outstanding as a
   * single commit rather than losing it. Reads the store at teardown time so it
   * cannot commit against a stale snapshot.
   */
  useEffect(() => () => {
    const state = useMapStore.getState();
    const current = state.roomKeys[objectId];
    const pending = draftRef.current;
    const updates: RoomKeyUpdate = {};
    let changed = false;
    for (const field of ROOM_KEY_TEXT_FIELDS) {
      if ((current?.[field] ?? "") !== pending[field]) {
        updates[field] = pending[field];
        changed = true;
      }
    }
    const senses: Partial<RoomKeySenses> = {};
    for (const field of ROOM_KEY_SENSE_FIELDS) {
      if ((current?.senses?.[field] ?? "") !== pending.senses[field]) {
        senses[field] = pending.senses[field];
        changed = true;
      }
    }
    if (Object.keys(senses).length > 0) updates.senses = senses;
    if (changed) state.setRoomKey(objectId, updates);
  }, [objectId]);

  const textField = (field: RoomKeyTextField, multiline: boolean) => {
    const dmOnly = ROOM_KEY_DM_ONLY_FIELDS.includes(field);
    return (
      <label key={field} className={labelClass}>
        <span className="flex items-center gap-1.5">
          {t(`roomKey.${field}`)}
          {dmOnly && (
            <span
              className="inline-flex items-center gap-1 rounded bg-[#d08bce]/15 px-1.5 py-0.5 text-[8px] font-bold tracking-wider text-[#d08bce]"
              title={t("roomKey.dmOnlyHint")}
            >
              <EyeOff size={9} /> {t("roomKey.dmOnly")}
            </span>
          )}
        </span>
        {multiline ? (
          <textarea
            value={draft[field]}
            rows={field === "dmNotes" ? 3 : 4}
            onChange={(event) => setTextDraft(field, event.target.value)}
            onBlur={() => commitText(field)}
            placeholder={t(`roomKey.${field}Placeholder`)}
            className={`${fieldClass} resize-y leading-5`}
          />
        ) : (
          <input
            value={draft[field]}
            onChange={(event) => setTextDraft(field, event.target.value)}
            onBlur={() => commitText(field)}
            placeholder={t(`roomKey.${field}Placeholder`)}
            className={`${fieldClass} h-10 py-0`}
          />
        )}
      </label>
    );
  };

  return (
    <aside className="flex max-h-full w-[min(360px,calc(100vw-24px))] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#1c1b18]/97 shadow-2xl shadow-black/30 backdrop-blur">
      <div className="flex shrink-0 items-start justify-between border-b border-white/8 px-4 py-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-xs font-semibold text-stone-100">
            <KeyRound size={14} className="text-[#d55b51]" /> {t("roomKey.title")}
          </p>
          <p className="mt-1 truncate text-[10px] text-stone-500">
            {orphaned
              ? t("roomKey.subtitleOrphan")
              : number
                ? t("roomKey.subtitle", { number })
                : t("roomKey.subtitleUnnumbered")}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-stone-500 hover:bg-white/5 hover:text-white"
          aria-label={t("roomKey.close")}
        >
          <X size={15} />
        </button>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {orphaned ? (
          <p className="flex items-start gap-2 rounded-xl border border-[#d0a03b]/25 bg-[#d0a03b]/10 px-3 py-2 text-[10px] leading-4 text-[#e0bd72]">
            <TriangleAlert size={13} className="mt-px shrink-0" /> {t("roomKey.orphanNotice")}
          </p>
        ) : (
          <div className="rounded-xl bg-white/[0.035] px-3 py-2">
            <p className={labelClass}>{t("roomKey.numberLabel")}</p>
            <p className="mt-1 flex items-center gap-1.5 font-serif text-lg font-bold text-stone-100">
              <Hash size={14} className="text-[#e27b71]" />
              {number || "—"}
            </p>
            <p className="mt-1 text-[9px] leading-4 text-stone-600">{t("roomKey.numberHint")}</p>
          </div>
        )}

        {textField("name", false)}
        {textField("description", true)}
        {textField("readAloud", true)}

        <section className="rounded-xl border border-white/6 bg-white/[0.02] p-2.5">
          <p className={labelClass}>{t("roomKey.senses")}</p>
          <p className="mt-1 text-[9px] leading-4 text-stone-600">{t("roomKey.sensesHint")}</p>
          <div className="mt-2 grid gap-2">
            {ROOM_KEY_SENSE_FIELDS.map((field) => (
              <label key={field} className={labelClass}>
                {t(`roomKey.${field}`)}
                <input
                  value={draft.senses[field]}
                  onChange={(event) => setSenseDraft(field, event.target.value)}
                  onBlur={() => commitSense(field)}
                  placeholder={t(`roomKey.${field}Placeholder`)}
                  className={`${fieldClass} h-9 py-0`}
                />
              </label>
            ))}
          </div>
        </section>

        {textField("lighting", false)}
        {textField("dmNotes", true)}
      </div>

      <p className="shrink-0 border-t border-white/8 px-4 py-2.5 text-[10px] text-stone-500">
        {t("roomKey.saveHint")}
      </p>
    </aside>
  );
}
